import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ThemeContext } from '../ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, CheckCircle2 } from 'lucide-react';
import api from '../api';

// No longer needed for Stripe Checkout

// Glassmorphism Card with Animations
const Card = ({ children, className = '', delay = 0, index = 0, onClick, isSelected, ...props }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  
  // Determine border color: selected state takes priority over hover
  const getBorderColor = () => {
    if (isSelected) return '#34c759';
    if (isHovered) return hexToRgba(hoverColor, 0.4);
    return isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)';
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      whileTap={onClick ? { scale: 0.98 } : {}}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={onClick}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 border transition-all duration-300 ${className} ${onClick ? 'cursor-pointer' : ''} ${isSelected ? 'border-[3px]' : ''}`}
      style={{
        boxShadow: isHovered
          ? `0 20px 60px ${hexToRgba(hoverColor, 0.25)}, 0 0 40px ${hexToRgba(hoverColor, 0.2)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
        borderColor: getBorderColor(),
        ...props.style, // Allow style override from props
      }}
      {...Object.fromEntries(Object.entries(props).filter(([key]) => key !== 'style'))}
    >
      <motion.div
        className="absolute inset-0 rounded-3xl opacity-0 pointer-events-none"
        animate={{
          opacity: isHovered ? 0.2 : 0,
        }}
        transition={{ duration: 0.3 }}
        style={{
          background: `linear-gradient(135deg, ${hexToRgba(hoverColor, 0.25)}, ${hexToRgba(hoverColor, 0.4)})`,
          filter: 'blur(20px)',
          zIndex: -1,
        }}
      />
      {children}
    </motion.div>
  );
};

const plans = [
  { 
    months: 1, 
    title: 'Monthly Plan',
    fallbackMonthlyPrice: 29.99,
    totalBilling: null,
    savings: 'Solid Value!',
    description: 'This subscription auto-renews.',
    features: ['Full access to all features', 'Rank-Up Competitions', 'Global Leaderboard', 'Practice questions']
  },
  { 
    months: 3, 
    title: 'Three-Month Plan',
    fallbackMonthlyPrice: 25.99,
    totalBilling: '$77.97 billed every 90 days',
    savings: 'A Great Deal!',
    description: 'This subscription auto-renews.',
    features: ['Full access to all features', 'Rank-Up Competitions', 'Global Leaderboard', 'Practice questions']
  },
  { 
    months: 6, 
    title: 'Six-Month Plan',
    fallbackMonthlyPrice: 19.99,
    totalBilling: '$119.94 billed every 180 days',
    savings: '33% Savings!',
    description: 'This subscription auto-renews.',
    features: ['Full access to all features', 'Rank-Up Competitions', 'Global Leaderboard', 'Practice questions']
  },
  { 
    months: 12, 
    title: 'Annual Plan',
    fallbackMonthlyPrice: 16.50,
    totalBilling: '$198.00 billed annually.',
    savings: 'Best Deal! 45% Savings!',
    description: 'This subscription does not auto-renew.',
    features: ['Full access to all features', 'Rank-Up Competitions', 'Global Leaderboard', 'Practice questions']
  },
];

const CheckoutForm = ({
  planMonths,
  onSuccess,
  onPaid,
  promotionCodeId,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { isDarkMode } = useContext(ThemeContext);
  const navigate = useNavigate();

  const handleCheckout = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const userStr = localStorage.getItem('sat_user');
      const user = userStr ? JSON.parse(userStr) : null;
      if (!user || !user.user_id) {
        setError('You must be logged in.');
        setLoading(false);
        return;
      }

      // Create Stripe Checkout session
      const response = await api.post('/api/stripe/create-checkout-session/', {
        plan_months: planMonths,
        promotion_code: promotionCodeId || null,
      });

      const { checkout_url } = response.data;

      // Redirect to Stripe Checkout
      window.location.href = checkout_url;

    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to create checkout session');
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      <motion.div 
        className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-xl p-3 sm:p-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-start sm:items-center space-x-2 sm:space-x-3">
          <div className="text-blue-500 dark:text-blue-400 text-xl sm:text-2xl flex-shrink-0">🔒</div>
          <div>
            <h3 className="text-sm sm:text-base font-semibold text-blue-900 dark:text-blue-200 mb-1">Secure Payment</h3>
            <p className="text-xs sm:text-sm text-blue-700 dark:text-blue-300">
              You'll be redirected to Stripe's secure checkout page with support for:
              <br />• Credit/Debit Cards (Visa, Mastercard, American Express)
            </p>
          </div>
        </div>
      </motion.div>

      <motion.button
        onClick={handleCheckout}
        disabled={loading}
        className="w-full py-3 sm:py-4 px-6 sm:px-8 bg-[#34c759] text-white text-base sm:text-xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 disabled:bg-gray-400 dark:disabled:bg-gray-600 transition-all"
        whileHover={!loading ? { scale: 1.02 } : {}}
        whileTap={!loading ? { scale: 0.98 } : {}}
      >
        {loading ? 'Redirecting to Secure Checkout...' : 'Continue to Secure Checkout'}
      </motion.button>

      {error && <motion.p className="text-sm sm:text-base text-red-500 dark:text-red-400 text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>{error}</motion.p>}
      {success && <motion.p className="text-sm sm:text-base text-green-600 dark:text-green-400 text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>{success}</motion.p>}
    </div>
  );
};

const SubscriptionInner = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selected, setSelected] = useState(1);
  const [subDetail, setSubDetail] = useState(null);
  
  // Check if user is logged in
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const u = localStorage.getItem('sat_user');
    return !!(u && JSON.parse(u));
  });
  
  const [isPremium, setIsPremium] = useState(() => {
    const u = localStorage.getItem('sat_user');
    try { return (u && JSON.parse(u)?.is_premium === true) || false; } catch { return false; }
  });
  const [trialUsed, setTrialUsed] = useState(() => {
    const u = localStorage.getItem('sat_user');
    try { return (u && JSON.parse(u)?.trial_used === true) || false; } catch { return false; }
  });
  const [loadingDetail, setLoadingDetail] = useState(isPremium);
  const [showCongrats, setShowCongrats] = useState(false);
  const [showTrialExpired, setShowTrialExpired] = useState(false);
  const [prices, setPrices] = useState({});
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState(null); // {promotion_code|coupon, percent_off, amount_off, currency}
  const [promoError, setPromoError] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentSyncError, setPaymentSyncError] = useState('');
  const [startingTrial, setStartingTrial] = useState(false);
  const [showTrialStarted, setShowTrialStarted] = useState(false);
  
  // Update login status when localStorage changes
  useEffect(() => {
    const checkLogin = () => {
      const u = localStorage.getItem('sat_user');
      const loggedIn = !!(u && JSON.parse(u));
      setIsLoggedIn(loggedIn);
      if (loggedIn) {
        try {
          const user = JSON.parse(u);
          setIsPremium(user?.is_premium === true);
          setTrialUsed(user?.trial_used === true);
        } catch {}
      } else {
        setIsPremium(false);
        setTrialUsed(false);
      }
    };
    checkLogin();
    // Listen for storage changes
    window.addEventListener('storage', checkLogin);
    return () => window.removeEventListener('storage', checkLogin);
  }, []);

  // Memoize title gradient
  const titleGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);

  // Check if user arrived from trial expiration
  useEffect(() => {
    if (searchParams.get('trial_expired') === 'true') {
      setShowTrialExpired(true);
      // Remove the query parameter from URL
      window.history.replaceState({}, '', '/subscription');
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isLoggedIn) {
      setLoadingDetail(false);
      setSubDetail(null);
      return;
    }
    
    const userStr = localStorage.getItem('sat_user');
    const user = userStr ? JSON.parse(userStr) : null;
    if (isPremium && user?.user_id) {
      setLoadingDetail(true);
      api.get(`/api/stripe/get-subscription/`)
        .then(res => setSubDetail(res.data.subscription || null))
        .catch(() => setSubDetail(null))
        .finally(() => setLoadingDetail(false));
    } else {
      setLoadingDetail(false);
      setSubDetail(null);
    }
  }, [isPremium, isLoggedIn]);

  useEffect(() => {
    api.get('/api/stripe/prices/').then(res => setPrices(res.data || {})).catch(() => setPrices({}));
  }, []);

  const handlePaymentSuccess = useCallback(async (sessionId) => {
    setPaymentSyncError('');
    setProcessingPayment(true);
    try {
      const response = await api.post('/api/stripe/checkout-success/', {
        session_id: sessionId
      });

      if (response.data.success) {
        // Update local storage
        const userStr = localStorage.getItem('sat_user');
        const user = userStr ? JSON.parse(userStr) : null;
        if (user) {
          const updated = { ...user, is_premium: true, trial_used: true };
          localStorage.setItem('sat_user', JSON.stringify(updated));
          setIsPremium(true);
          setTrialUsed(true);
          setShowCongrats(true);
        }

        // Clean up URL
        window.history.replaceState({}, document.title, '/subscription');
      } else {
        setPaymentSyncError('We could not activate your subscription. Please retry.');
      }
    } catch (error) {
      console.error('Failed to confirm payment:', error);
      setPaymentSyncError(
        error?.response?.data?.error ||
        'Stripe completed checkout, but we could not activate your subscription. Please retry.'
      );
    } finally {
      setProcessingPayment(false);
    }
  }, [setIsPremium, setShowCongrats]);

  // Handle successful payment return from Stripe Checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const sessionId = urlParams.get('session_id');

    if (success && sessionId) {
      // Handle successful payment
      handlePaymentSuccess(sessionId);
    }
  }, [handlePaymentSuccess]);

  const applyPromo = async () => {
    if (!promoInput.trim()) {
      setPromoError('');
      setPromo(null);
      return;
    }
    
    try {
      const { data } = await api.post('/api/stripe/validate-promo/', { code: promoInput.trim() });
      if (data.valid) {
        setPromo(data);
        setPromoError('');
      } else {
        setPromo(null);
        setPromoError('Invalid promo code');
      }
    } catch (err) {
      setPromo(null);
      setPromoError('Invalid promo code');
    }
  };

  const formatUnit = (p) => {
    if (!p) return '';
    const count = p.interval_count || 1;
    const interval = p.interval;
    if (interval === 'month') return count === 1 ? '/Month' : `/Month`;
    if (interval === 'year') return '/Month';
    if (interval === 'day') return count === 1 ? '/ day' : ` / ${count} days`;
    return '';
  };

  const computePriceLabel = (months) => {
    const p = prices[months];
    const plan = plans.find(plan => plan.months === months);
    const fallbackMonthlyPrice = Number(plan?.fallbackMonthlyPrice);
    const hasStripePrice = p?.unit_amount !== null && p?.unit_amount !== undefined && Number.isFinite(Number(p.unit_amount));
    const base = hasStripePrice ? Number(p.unit_amount) / 100 : fallbackMonthlyPrice * months;
    const unit = p ? formatUnit(p) || '/Month' : '/Month';

    if (!Number.isFinite(base)) {
      return {
        price: '0.00',
        originalPrice: null,
        unit: '/Month',
        hasDiscount: false,
        discountText: null
      };
    }

    if (promo?.percent_off) {
      const off = Math.round(base * (promo.percent_off / 100) * 100) / 100;
      const discounted = Math.round((base - off) * 100) / 100;
      const strike = base.toFixed(2);
      return {
        price: discounted.toFixed(2),
        originalPrice: strike,
        unit: unit,
        hasDiscount: true,
        discountText: `(${promo.percent_off}% off)`
      };
    }
    if (promo?.amount_off) {
      const discounted = Math.max(0, base - (promo.amount_off / 100));
      const strike = base.toFixed(2);
      return {
        price: discounted.toFixed(2),
        originalPrice: strike,
        unit: unit,
        hasDiscount: true,
        discountText: `(${(promo.amount_off/100).toFixed(2)} ${promo.currency || ''} off)`
      };
    }
    return {
      price: base.toFixed(2),
      originalPrice: null,
      unit: unit,
      hasDiscount: false,
      discountText: null
    };
  };

  const computeTotalBilling = (months) => {
    const priceInfo = computePriceLabel(months);
    if (!priceInfo) return null;
    
    // computePriceLabel returns the total price for the billing period
    const discountedTotal = parseFloat(priceInfo.price);
    const originalTotal = priceInfo.originalPrice ? parseFloat(priceInfo.originalPrice) : null;
    
    // Format based on months
    let billingText = '';
    if (months === 3) {
      billingText = `$${discountedTotal.toFixed(2)} billed every 90-days`;
      if (originalTotal && originalTotal !== discountedTotal) {
        billingText += ` (was $${originalTotal.toFixed(2)})`;
      }
    } else if (months === 6) {
      billingText = `$${discountedTotal.toFixed(2)} billed every 180 days`;
      if (originalTotal && originalTotal !== discountedTotal) {
        billingText += ` (was $${originalTotal.toFixed(2)})`;
      }
    } else if (months === 12) {
      billingText = `$${discountedTotal.toFixed(2)} billed annually.`;
      if (originalTotal && originalTotal !== discountedTotal) {
        billingText += ` (was $${originalTotal.toFixed(2)})`;
      }
    }
    else {
      return null;
    }
    
    return billingText;
  };

  const handleCancelAutoRenew = async () => {
    try {
      const userStr = localStorage.getItem('sat_user');
      const user = userStr ? JSON.parse(userStr) : null;
      await api.post('/api/stripe/cancel-subscription/');
      const refreshed = await api.get(`/api/stripe/get-subscription/`);
      setSubDetail(refreshed.data.subscription || null);
      alert('Auto-renewal cancelled. Your subscription will end at the period end date.');
    } catch (e) {
      alert(e?.response?.data?.error || e?.response?.data?.detail || 'Cancel failed');
    }
  };

  const handleEnableAutoRenew = async () => {
    try {
      const userStr = localStorage.getItem('sat_user');
      const user = userStr ? JSON.parse(userStr) : null;
      await api.post('/api/stripe/resume-subscription/');
      const refreshed = await api.get(`/api/stripe/get-subscription/`);
      setSubDetail(refreshed.data.subscription || null);
      alert('Auto-renewal enabled. Your subscription will continue after the period end date.');
    } catch (e) {
      alert(e?.response?.data?.error || e?.response?.data?.detail || 'Resume failed');
    }
  };
  const handleStartFreeTrial = async () => {
    setStartingTrial(true);
    try {
      const response = await api.post('/api/stripe/start-free-trial/');
      if (response.data.success) {
        const userStr = localStorage.getItem('sat_user');
        const user = userStr ? JSON.parse(userStr) : null;
        if (user) {
          const updated = { ...user, is_premium: true, trial_used: true };
          localStorage.setItem('sat_user', JSON.stringify(updated));
          setIsPremium(true);
          setTrialUsed(true);
        }
        setShowTrialStarted(true);
      }
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to start free trial. Please try again.');
    } finally {
      setStartingTrial(false);
    }
  };

  // Show processing indicator if we're processing a successful payment
  if (processingPayment) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <Card delay={0} className="p-4 sm:p-6 md:p-8 max-w-md w-full text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="inline-block mb-4"
          >
            <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 mx-auto" style={{ borderColor: '#34c759' }}></div>
          </motion.div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2 font-zonaProUltra">Processing Payment</h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 font-zonaProRegular">Please wait while we confirm your subscription...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center font-zonaProRegular p-4 pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {paymentSyncError && (
          <div
            role="alert"
            className="mt-16 mb-6 flex flex-col sm:flex-row items-center justify-between gap-4 border border-red-300 bg-red-50 px-5 py-4 text-red-800 dark:border-red-700 dark:bg-red-950/40 dark:text-red-200"
          >
            <p className="text-sm sm:text-base font-zonaProRegular">{paymentSyncError}</p>
            <button
              type="button"
              onClick={() => {
                const sessionId = new URLSearchParams(window.location.search).get('session_id');
                if (sessionId) handlePaymentSuccess(sessionId);
              }}
              className="shrink-0 border border-red-400 bg-white px-5 py-2 font-zonaProUltra text-red-700 transition-colors hover:bg-red-100 dark:border-red-600 dark:bg-gray-900 dark:text-red-200 dark:hover:bg-red-950"
            >
              Retry
            </button>
          </div>
        )}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className={`text-center mb-6 sm:mb-8 ${paymentSyncError ? 'mt-6' : 'mt-16 sm:mt-16 md:mt-16'}`}
        >
          <h1
            key={`subscription-title-${isDarkMode}`}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold font-zonaProUltra mb-3 sm:mb-4 relative inline-block"
            style={{
              background: titleGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            {isPremium ? 'Subscription Details' : 'Game Your SAT'}
          </h1>
          {!isPremium && (
          <h1
            key={`subscription-title-${isDarkMode}`}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold font-zonaProUltra mb-3 sm:mb-4 relative inline-block"
            style={{
              background: titleGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            {'Your Level-Up Starts Now'}
          </h1>
          )}
        </motion.div>

        {/* If premium: show details (with loading state). If not: show purchase UI. */}
        {isPremium ? (
          loadingDetail ? (
            <Card delay={0.2} className="p-4 sm:p-6 text-center max-w-xl mx-auto">
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 font-zonaProRegular">Loading subscription...</p>
            </Card>
          ) : subDetail?.is_free_trial ? (
            /* Free trial view: trial summary + plan picker + payment */
            <div className="w-full max-w-7xl mx-auto flex flex-col gap-8">
              <Card delay={0.2} className="p-6 sm:p-8 max-w-xl mx-auto w-full text-center border-[3px] border-[#34c759]">
                <div className="text-4xl mb-3">🎉</div>
                <h2 className="text-xl sm:text-2xl font-bold font-zonaProUltra mb-4" style={{ color: '#34c759' }}>Free Trial Active</h2>
                <div className="space-y-3 text-left">
                  <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-500 dark:text-gray-400 font-zonaProRegular">Status</span>
                    <span className="text-sm font-semibold text-[#34c759] font-zonaProRegular">Free Trial</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-500 dark:text-gray-400 font-zonaProRegular">Trial Ends</span>
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 font-zonaProRegular">
                      {subDetail.current_period_end ? new Date(subDetail.current_period_end * 1000).toLocaleDateString() : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400 font-zonaProRegular">Days Remaining</span>
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 font-zonaProRegular">
                      {subDetail.current_period_end ? Math.max(0, Math.ceil((subDetail.current_period_end * 1000 - Date.now()) / (1000 * 60 * 60 * 24))) : '-'} days
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-4 font-zonaProRegular">
                  Subscribe below to keep full access after your trial ends.
                </p>
              </Card>

              {/* Plan cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                {plans.map((p, idx) => {
                  const priceInfo = computePriceLabel(p.months);
                  const isHighlighted = selected === p.months;
                  return (
                    <motion.div
                      key={p.months}
                      initial={{ opacity: 0, y: 50 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.2 + idx * 0.1 }}
                      whileHover={{ scale: 1.05, y: -5 }}
                      onClick={(e) => { e.stopPropagation(); setSelected(p.months); }}
                      className="cursor-pointer"
                    >
                      <Card delay={0} isSelected={isHighlighted} className={`h-full flex flex-col relative ${isHighlighted ? 'ring-4 ring-[#34c759] bg-[#34c759]/5 dark:bg-[#34c759]/10' : ''}`} onClick={undefined}>
                        {isHighlighted && (
                          <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.3, type: "spring" }} className="absolute top-3 right-3 z-10">
                            <CheckCircle2 size={28} className="text-[#34c759]" fill="#34c759" stroke="white" strokeWidth={2} />
                          </motion.div>
                        )}
                        <div className="flex flex-col h-full">
                          <h3 className="text-xl md:text-2xl font-bold font-zonaProUltra mb-3 relative inline-block" style={{ background: titleGradient, backgroundSize: '200% 100%', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'shine 10s linear infinite', lineHeight: '1.2', paddingBottom: '0.25rem' }}>{p.title}</h3>
                          <div className="mb-3">
                            <div className="flex items-baseline gap-1">
                              <span className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra text-gray-900 dark:text-gray-100">${(parseFloat(priceInfo.price) / p.months).toFixed(2)}</span>
                              {priceInfo.originalPrice && <span className="text-lg md:text-xl font-zonaProRegular text-red-600 line-through ml-2">${(parseFloat(priceInfo.originalPrice) / p.months).toFixed(2)}</span>}
                              <span className="text-lg md:text-xl font-zonaProRegular text-gray-600 dark:text-gray-400">{priceInfo.unit}</span>
                            </div>
                            {(() => { const ct = computeTotalBilling(p.months); const db = ct !== null ? ct : p.totalBilling; return db !== null ? <p className="text-sm md:text-base font-zonaProRegular text-gray-500 dark:text-gray-400 mt-1">{db}</p> : <p className="text-sm md:text-base font-zonaProRegular text-white dark:text-black mt-1">.</p>; })()}
                          </div>
                          {!promo && p.savings !== null ? <div className="mb-4"><span className={`inline-block px-3 py-1 rounded-full text-sm md:text-base font-bold font-zonaProUltra ${p.savings.includes('Best Deal') ? 'bg-[#06bae4] text-white' : 'bg-[#34c759] text-white'}`}>{p.savings}</span></div> : <div className="mb-4"><span className="text-sm font-bold font-zonaProUltra text-white dark:text-black">.</span></div>}
                          <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 font-zonaProRegular mb-4">{p.description}</p>
                          <Link to="/terms" className="text-base italic underline text-gray-600 dark:text-gray-400 hover:text-[#06bae4] transition-colors font-zonaProRegular">Cancel anytime</Link>
                        </div>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>

              {/* Promo + Payment */}
              <div className="w-full max-w-6xl grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 mx-auto">
                <Card delay={0.4} className="p-4 sm:p-6">
                  <h2 className="text-xl sm:text-2xl font-bold font-zonaProUltra mb-3 sm:mb-4 relative inline-block" style={{ background: titleGradient, backgroundSize: '200% 100%', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'shine 10s linear infinite', lineHeight: '1.2', paddingBottom: '0.25rem' }}>Promo Code</h2>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-3 font-zonaProRegular">Have a promo code? Apply it to get the discount.</p>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-3">
                    <input value={promoInput} onChange={e => { setPromoInput(e.target.value); setPromoError(''); }} placeholder="Enter promo code" className={`border rounded-lg px-4 py-3 sm:py-2 flex-1 bg-transparent dark:text-gray-100 text-gray-900 font-zonaProRegular text-base sm:text-sm focus:outline-none focus:ring-2 ${promoError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-green-500'}`} />
                    <motion.button onClick={applyPromo} className="py-3 sm:py-2 px-6 sm:px-5 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-zonaProUltra text-base sm:text-sm rounded-full hover:shadow-lg hover:shadow-cyan-500/50 transition-all whitespace-nowrap" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>Apply</motion.button>
                  </div>
                  {promo && <motion.div className="text-sm sm:text-base text-green-700 dark:text-green-400 font-zonaProRegular mb-2" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>Applied {promo.percent_off ? `${promo.percent_off}%` : promo.amount_off ? `${(promo.amount_off/100).toFixed(2)} ${promo.currency || ''}` : ''} discount.</motion.div>}
                  {promoError && <motion.div className="text-sm sm:text-base text-red-600 dark:text-red-400 font-zonaProRegular mb-2" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>{promoError}</motion.div>}
                  {prices[selected] && (() => { const priceInfo = computePriceLabel(selected); return (<div className="mt-4"><div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 font-zonaProRegular">Selected Plan: <span className="font-semibold">{plans.find(pl => pl.months === selected)?.title}</span></div><div className="text-base sm:text-lg mt-1 font-zonaProRegular" style={{ color: isDarkMode ? '#ff9e42' : '#06b6d4' }}>${priceInfo.price}{priceInfo.originalPrice && <span className="ml-2 text-red-600 line-through">${priceInfo.originalPrice}</span>}</div></div>); })()}
                </Card>
                <Card delay={0.5} className="p-4 sm:p-6">
                  <h2 className="text-xl sm:text-2xl font-bold font-zonaProUltra mb-3 sm:mb-4 relative inline-block" style={{ background: titleGradient, backgroundSize: '200% 100%', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'shine 10s linear infinite', lineHeight: '1.2', paddingBottom: '0.25rem' }}>Payment Details</h2>
                  {prices[selected] && <div className="mb-3 text-base sm:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular">{(() => { const p = prices[selected]; const base = (p.unit_amount || 0) / 100; let charge = base; if (promo?.percent_off) charge = Math.round(base * (1 - promo.percent_off / 100) * 100) / 100; if (promo?.amount_off) charge = Math.max(0, base - (promo.amount_off / 100)); return <span>Pay <span className="font-bold" style={{ color: '#ff7a00' }}>${charge.toFixed(2)}</span></span>; })()}</div>}
                  <CheckoutForm planMonths={selected} onSuccess={setSubDetail} onPaid={() => setShowCongrats(true)} promotionCodeId={promo?.promotion_code || promo?.coupon || null} />
                </Card>
              </div>
            </div>
          ) : (
            /* Paid subscription details */
            <Card delay={0.2} className="p-4 sm:p-6 space-y-3 sm:space-y-4 max-w-xl mx-auto">
              {!subDetail ? (
                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-zonaProRegular">No active subscription.</p>
              ) : (
                <>
                  <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 font-zonaProRegular">Plan</span>
                    <span className="text-sm sm:text-base font-semibold font-zonaProRegular text-gray-800 dark:text-gray-200">{subDetail.plan_months || '-'} months</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 font-zonaProRegular">Status</span>
                    <span className={`text-sm sm:text-base font-semibold capitalize font-zonaProRegular ${subDetail.status === 'active' ? 'text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`}>
                      {subDetail.status === 'trialing' ? 'Trial' : subDetail.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 font-zonaProRegular">Current Period Start</span>
                    <span className="text-xs sm:text-sm md:text-base font-semibold font-zonaProRegular text-gray-800 dark:text-gray-200">{subDetail.current_period_start ? new Date(subDetail.current_period_start * 1000).toLocaleDateString() : '-'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 font-zonaProRegular">Current Period End</span>
                    <span className="text-xs sm:text-sm md:text-base font-semibold font-zonaProRegular text-gray-800 dark:text-gray-200">{subDetail.current_period_end ? new Date(subDetail.current_period_end * 1000).toLocaleDateString() : '-'}</span>
                  </div>
                  {subDetail.cancel_at_period_end ? (
                    <div>
                      <motion.div
                        className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-xl"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                      >
                        <div className="flex items-start">
                          <div className="text-yellow-500 dark:text-yellow-400 text-2xl mr-3">⚠️</div>
                          <div>
                            <h3 className="font-semibold text-yellow-900 dark:text-yellow-200 mb-1 font-zonaProUltra">Auto-Renewal Cancelled</h3>
                            <p className="text-sm text-yellow-800 dark:text-yellow-300 font-zonaProRegular">
                              Your subscription will not renew and you'll lose premium access on{' '}
                              <span className="font-bold">{subDetail.current_period_end ? new Date(subDetail.current_period_end * 1000).toLocaleDateString() : 'the end date'}</span>.
                            </p>
                            <p className="text-sm text-yellow-800 dark:text-yellow-300 mt-2 font-zonaProRegular">
                              You can continue to use premium features until then. After that, you'll be automatically downgraded to the free tier.
                            </p>
                          </div>
                        </div>
                      </motion.div>
                      <motion.button onClick={handleEnableAutoRenew} className="mt-4 py-2 sm:py-3 px-4 sm:px-8 bg-[#34c759] text-white text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 transition-all" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>Enable Auto-Renew</motion.button>
                    </div>
                  ) : (
                    <motion.button onClick={handleCancelAutoRenew} className="mt-2 py-2 sm:py-3 px-4 sm:px-8 bg-gradient-to-r from-red-500 to-red-600 text-white text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-red-500/50 transition-all" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>Cancel Auto-Renew</motion.button>
                  )}
                </>
              )}
            </Card>
          )
        ) : (
          <>
            {/* Main content above pricing cards */}
            {!isPremium && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-center mb-8 md:mb-12 max-w-4xl mx-auto"
              >
                <h2
                  key={`subscription-h2-${isDarkMode}`}
                  className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra mb-4 relative inline-block"
                  style={{
                    background: titleGradient,
                    backgroundSize: '200% 100%',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    animation: 'shine 10s linear infinite',
                    lineHeight: '1.2',
                    paddingBottom: '0.25rem'
                  }}
                >
                  Authentic practice on your schedule.
                </h2>
                <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 mb-6 font-zonaProRegular">
                  Game Your SAT features flexible pricing, so you can practice at your own pace. Invite friends to play competitively — and earn points!
                </p>
                <div className="mb-6">
                  <h3
                    key={`free-trial-h3-${isDarkMode}`}
                    className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra mb-3 relative inline-block"
                    style={{
                      background: titleGradient,
                      backgroundSize: '200% 100%',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      animation: 'shine 10s linear infinite',
                      lineHeight: '1.2',
                      paddingBottom: '0.25rem'
                    }}
                  >
                    Free for 7 days!
                  </h3>
                  <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular">
                    Start your 7-day free trial of Game Your SAT. Explore everything, see your progress, and pick the plan that works for you. Your chosen subscription starts automatically after the trial.                  </p>
                </div>
              </motion.div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 max-w-8xl mx-auto mb-12">
              {plans.map((p, idx) => {
                const priceInfo = computePriceLabel(p.months);
                const isHighlighted = selected === p.months;
                return (
                <motion.div
                  key={p.months}
                    initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 + idx * 0.1 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelected(p.months);
                    }}
                    className="cursor-pointer"
                  >
                    <Card
                      delay={0}
                      isSelected={isHighlighted}
                      className={`h-full flex flex-col relative ${isHighlighted ? 'ring-4 ring-[#34c759] bg-[#34c759]/5 dark:bg-[#34c759]/10' : ''}`}
                      onClick={undefined}
                    >
                      {/* Selected Indicator - Green Checkmark in Top Right */}
                      {isHighlighted && (
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ duration: 0.3, type: "spring" }}
                          className="absolute top-3 right-3 z-10"
                        >
                          <CheckCircle2 
                            size={28} 
                            className="text-[#34c759]" 
                            fill="#34c759"
                            stroke="white"
                            strokeWidth={2}
                          />
                        </motion.div>
                      )}
                      <div className="flex flex-col h-full">
                        {/* Plan Name */}
                        <h3
                          key={`plan-title-${p.months}-${isDarkMode}`}
                          className="text-xl md:text-2xl font-bold font-zonaProUltra mb-3 relative inline-block"
                          style={{
                            background: titleGradient,
                            backgroundSize: '200% 100%',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            animation: 'shine 10s linear infinite',
                            lineHeight: '1.2',
                            paddingBottom: '0.25rem'
                          }}
                        >
                          {p.title}
                        </h3>

                        {/* Price */}
                        <div className="mb-3">
                          <div className="flex items-baseline gap-1">
                            <span className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra text-gray-900 dark:text-gray-100">
                              ${(parseFloat(priceInfo.price) / p.months).toFixed(2)}
                            </span>
                            {priceInfo.originalPrice && (
                              <span className="text-lg md:text-xl font-zonaProRegular text-red-600 line-through ml-2">
                                ${(parseFloat(priceInfo.originalPrice) / p.months).toFixed(2)}
                              </span>
                            )}
                            <span className="text-lg md:text-xl font-zonaProRegular text-gray-600 dark:text-gray-400">
                              {priceInfo.unit}
                            </span>
                          </div>
                          {/* Total Billing Info */}
                          {(() => {
                            const computedTotalBilling = computeTotalBilling(p.months);
                            const displayBilling = computedTotalBilling !== null ? computedTotalBilling : p.totalBilling;
                            return displayBilling !== null ? (
                              <p className="text-sm md:text-base font-zonaProRegular text-gray-500 dark:text-gray-400 mt-1">
                                {displayBilling}
                              </p>
                            ) : (
                              <p className="text-sm md:text-base font-zonaProRegular text-white dark:text-black mt-1">
                               .
                             </p>
                            );
                          })()}
                          {priceInfo.hasDiscount && priceInfo.discountText && (
                            <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                              {priceInfo.discountText}
                            </p>
                          )}
                        </div>

                        {/* Savings Badge */}
                        {!promo && p.savings !== null ? (
                          <div className="mb-4">
                            <span className={`inline-block px-3 py-1 rounded-full text-sm md:text-base font-bold font-zonaProUltra ${
                              p.savings.includes('Best Deal') 
                                ? 'bg-[#06bae4] text-white dark:bg-[#06bae4] dark:text-white'
                                : 'bg-[#34c759] text-white dark:bg-[#34c759] dark:text-white'
                            }`}>
                              {p.savings}
                            </span>
                          </div>
                        ) : (
                          <div className="mb-4">
                            <span className="text-sm md:text-base font-bold font-zonaProUltra text-white dark:text-black">
                              .
                            </span>
                          </div>
                        )}

                        {/* Description */}
                        <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 font-zonaProRegular mb-4">
                          {p.description}
                        </p>
                        {/* Cancel Anytime */}
                        <Link
                           to="/terms"
                           className="text-base italic underline text-gray-600 dark:text-gray-400 hover:text-[#06bae4] dark:hover:text-[#06bae4] transition-colors font-zonaProRegular"
                          >
                           Cancel anytime
                         </Link>
                      </div>
                  </Card>
                </motion.div>
                );
              })}
            </div>
            {isLoggedIn ? (
              <div className="w-full max-w-6xl flex flex-col gap-6 mb-8 mx-auto">
                {/* Free Trial Card — only shown to eligible users */}
                {!trialUsed && !isPremium && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    <Card delay={0} className="p-6 sm:p-8 text-center border-[3px] border-[#34c759]">
                      <div className="text-5xl mb-3">🎉</div>
                      <h2
                        className="text-2xl sm:text-3xl font-bold font-zonaProUltra mb-2 relative inline-block"
                        style={{
                          background: titleGradient,
                          backgroundSize: '200% 100%',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          animation: 'shine 10s linear infinite',
                          lineHeight: '1.2',
                          paddingBottom: '0.25rem'
                        }}
                      >
                        Start Your Free Trial
                      </h2>
                      <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 font-zonaProRegular mb-2">
                        Get <span className="font-bold text-[#34c759]">7 days of full access</span> — no credit card required.
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 font-zonaProRegular mb-6">
                        Explore all features. If you love it, subscribe before your trial ends.
                      </p>
                      <motion.button
                        onClick={handleStartFreeTrial}
                        disabled={startingTrial}
                        className="py-3 sm:py-4 px-10 sm:px-14 bg-[#34c759] text-white text-lg sm:text-xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 disabled:bg-gray-400 dark:disabled:bg-gray-600 transition-all"
                        whileHover={!startingTrial ? { scale: 1.04 } : {}}
                        whileTap={!startingTrial ? { scale: 0.97 } : {}}
                      >
                        {startingTrial ? 'Starting Trial…' : 'Start Free Trial — No Credit Card Needed'}
                      </motion.button>
                    </Card>
                  </motion.div>
                )}

                {/* Paid plan section */}
                {!trialUsed && !isPremium && (
                  <div className="text-center text-gray-500 dark:text-gray-400 font-zonaProRegular text-sm sm:text-base">
                    — or subscribe directly to a paid plan below —
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card delay={0.4} className="p-4 sm:p-6">
                  <h2
                    key={`promo-code-${isDarkMode}`}
                    className="text-xl sm:text-2xl font-bold font-zonaProUltra mb-3 sm:mb-4 relative inline-block"
                    style={{
                      background: titleGradient,
                      backgroundSize: '200% 100%',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      animation: 'shine 10s linear infinite',
                      lineHeight: '1.2',
                      paddingBottom: '0.25rem'
                    }}
                  >
                    Promo Code
                  </h2>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-3 font-zonaProRegular">Have a promo code? Apply it to get the discount.</p>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-3">
                    <input 
                      value={promoInput} 
                      onChange={e => {
                        setPromoInput(e.target.value);
                        setPromoError(''); // Clear error when user types
                      }}
                      placeholder="Enter promo code" 
                      className={`border rounded-lg px-4 py-3 sm:py-2 flex-1 bg-transparent dark:text-gray-100 text-gray-900 font-zonaProRegular text-base sm:text-sm focus:outline-none focus:ring-2 ${
                        promoError 
                          ? 'border-red-500 dark:border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 dark:border-gray-600 focus:ring-green-500'
                      }`}
                    />
                    <motion.button 
                      onClick={applyPromo} 
                      className="py-3 sm:py-2 px-6 sm:px-5 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-zonaProUltra text-base sm:text-sm rounded-full hover:shadow-lg hover:shadow-cyan-500/50 transition-all whitespace-nowrap"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Apply
                    </motion.button>
                  </div>
                  {promo && (
                    <motion.div 
                      className="text-sm sm:text-base text-green-700 dark:text-green-400 font-zonaProRegular mb-2"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      Applied {promo.percent_off ? `${promo.percent_off}%` : promo.amount_off ? `${(promo.amount_off/100).toFixed(2)} ${promo.currency || ''}` : ''} discount.
                    </motion.div>
                  )}
                  {promoError && (
                    <motion.div 
                      className="text-sm sm:text-base text-red-600 dark:text-red-400 font-zonaProRegular mb-2"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      {promoError}
                    </motion.div>
                  )}
                  {prices[selected] && (() => {
                    const priceInfo = computePriceLabel(selected);
                    return (
                    <div className="mt-4">
                      <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 font-zonaProRegular">Selected Plan: <span className="font-semibold">{plans.find(pl => pl.months === selected)?.title}</span></div>
                        <div className="text-base sm:text-lg mt-1 font-zonaProRegular break-words" style={{ color: isDarkMode ? '#ff9e42' : '#06b6d4' }}>
                          ${priceInfo.price}
                          {priceInfo.originalPrice && (
                            <span className="ml-2 text-red-600 line-through">${priceInfo.originalPrice}</span>
                          )}
                          {priceInfo.hasDiscount && priceInfo.discountText && (
                            <span className="ml-2 text-red-600">{priceInfo.discountText}</span>
                          )}
                        </div>
                    </div>
                    );
                  })()}
                </Card>
                <Card delay={0.5} className="p-4 sm:p-6">
                  <h2
                    key={`payment-details-${isDarkMode}`}
                    className="text-xl sm:text-2xl font-bold font-zonaProUltra mb-3 sm:mb-4 relative inline-block"
                    style={{
                      background: titleGradient,
                      backgroundSize: '200% 100%',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      animation: 'shine 10s linear infinite',
                      lineHeight: '1.2',
                      paddingBottom: '0.25rem'
                    }}
                  >
                    Payment Details
                  </h2>
                  {prices[selected] && (
                    <div className="mb-3 text-base sm:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular">
                      {(() => {
                        const p = prices[selected];
                        const base = (p.unit_amount || 0) / 100;
                        let charge = base;
                        if (promo?.percent_off) charge = Math.round(base * (1 - promo.percent_off / 100) * 100) / 100;
                        if (promo?.amount_off) charge = Math.max(0, base - (promo.amount_off / 100));
                        const unit = formatUnit(p);
                        return <span>Pay <span className="font-bold" style={{ color: '#ff7a00' }}>${charge.toFixed(2)}</span></span>;
                      })()}
                    </div>
                  )}
                  <CheckoutForm
                    planMonths={selected}
                    onSuccess={setSubDetail}
                    onPaid={() => setShowCongrats(true)}
                    promotionCodeId={promo?.promotion_code || promo?.coupon || null}
                  />
                </Card>
                </div>
              </div>
            ) : (
              <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="text-center"
        >
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link
              to="/signup"
              className="inline-block px-8 py-4 bg-[#212D82] dark:bg-[#ff7a00] hover:bg-[#1a2469] dark:hover:bg-[#e66900] text-white rounded-full font-bold font-zonaProUltra text-lg md:text-xl transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Level Up Now
            </Link>
          </motion.div>
          </div>
        </motion.div>
            )}
            <AnimatePresence>
              {showCongrats && (
                <motion.div 
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => { setShowCongrats(false); window.location.href = '/options'; }}
                >
                  <motion.div 
                    className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 rounded-3xl shadow-2xl p-8 max-w-md w-full mx-4 text-center border border-white/20 dark:border-gray-700/50"
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h3
                      key={`payment-successful-${isDarkMode}`}
                      className="text-2xl font-zonaProUltra mb-2 relative inline-block"
                      style={{
                        background: titleGradient,
                        backgroundSize: '200% 100%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        animation: 'shine 10s linear infinite',
                        lineHeight: '1.2',
                        paddingBottom: '0.25rem'
                      }}
                    >
                      Payment Successful
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-6 font-zonaProRegular">All features are now unlocked.</p>
                    <motion.button 
                      onClick={() => { setShowCongrats(false); window.location.href = '/options'; }} 
                      className="py-3 px-8 bg-[#34c759] text-white text-xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 transition-all"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Go to Home
                    </motion.button>
                  </motion.div>
                </motion.div>
              )}
              {showTrialStarted && (
                <motion.div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => { setShowTrialStarted(false); window.location.href = '/options'; }}
                >
                  <motion.div
                    className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 rounded-3xl shadow-2xl p-8 max-w-md w-full mx-4 text-center border border-white/20 dark:border-gray-700/50"
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="text-5xl mb-4">🎉</div>
                    <h3
                      key={`trial-started-${isDarkMode}`}
                      className="text-2xl font-zonaProUltra mb-2 relative inline-block"
                      style={{
                        background: titleGradient,
                        backgroundSize: '200% 100%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        animation: 'shine 10s linear infinite',
                        lineHeight: '1.2',
                        paddingBottom: '0.25rem'
                      }}
                    >
                      Free Trial Started!
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-2 font-zonaProRegular">
                      You have <span className="font-bold text-[#34c759]">7 days of full access</span>.
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 font-zonaProRegular">
                      No credit card needed. Subscribe anytime before your trial ends to keep access.
                    </p>
                    <motion.button
                      onClick={() => { setShowTrialStarted(false); window.location.href = '/options'; }}
                      className="py-3 px-8 bg-[#34c759] text-white text-xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 transition-all"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Start Playing
                    </motion.button>
                  </motion.div>
                </motion.div>
              )}
              {showTrialExpired && (
                <motion.div 
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowTrialExpired(false)}
                >
                  <motion.div 
                    className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 rounded-3xl shadow-2xl p-8 max-w-md w-full mx-4 text-center border border-white/20 dark:border-gray-700/50"
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h3
                      key={`trial-expired-${isDarkMode}`}
                      className="text-2xl font-zonaProUltra mb-2 relative inline-block"
                      style={{
                        background: titleGradient,
                        backgroundSize: '200% 100%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        animation: 'shine 10s linear infinite',
                        lineHeight: '1.2',
                        paddingBottom: '0.25rem'
                      }}
                    >
                      Free Trial Period Ended
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-6 font-zonaProRegular">Your free trial period has ended. You need to subscribe to use the premium features.</p>
                    <motion.button 
                      onClick={() => setShowTrialExpired(false)} 
                      className="py-3 px-8 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white text-xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-cyan-500/50 transition-all"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Got it
                    </motion.button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    </div>
  );
};

const SubscriptionPage = () => {
  return <SubscriptionInner />;
};

export default SubscriptionPage;
